/**
 * SportsTournamentNew — /sports/tournaments/new
 *
 * Creates a tournament. Signed-in users only. Photo URLs (comma-
 * separated for MVP) and video URLs via VideoLinkManager.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trophy, Save, Image as ImageIcon } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SportsCategoryNav from "@/components/SportsCategoryNav";
import VideoLinkManager from "@/components/VideoLinkManager";

const empty = {
  name: "", sport: "", format: "", description: "",
  venue_name: "", city: "", state: "", country: "",
  start_date: "", end_date: "", registration_deadline: "",
  entry_fee_amount: "", entry_fee_currency: "USD",
  rules: "", categories: "", cover_image_url: "",
  photo_urls: "", contact_email: "", contact_phone: "",
};

export default function SportsTournamentNew() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [videoUrls, setVideoUrls] = useState([]);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { http.get("/sports/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Sign in to create a tournament.");
      nav(`/login?redirect=${encodeURIComponent("/sports/tournaments/new")}`, { replace: true });
    }
  }, [authLoading, user, nav]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.sport) { toast.error("Name and sport are required."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), sport: form.sport,
        format: form.format || null,
        description: form.description || null,
        venue_name: form.venue_name || null,
        city: form.city || null, state: form.state || null, country: form.country || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        registration_deadline: form.registration_deadline || null,
        entry_fee_amount: form.entry_fee_amount ? Number(form.entry_fee_amount) : null,
        entry_fee_currency: form.entry_fee_currency || "USD",
        rules: form.rules || null,
        categories: form.categories ? form.categories.split(",").map(s => s.trim()).filter(Boolean) : [],
        cover_image_url: form.cover_image_url || null,
        photo_urls: form.photo_urls ? form.photo_urls.split(/[\n,]/).map(s => s.trim()).filter(Boolean) : [],
        video_urls: videoUrls,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
      };
      const r = await http.post("/sports/tournaments", payload);
      toast.success("Tournament published!");
      nav(`/sports/tournaments/${r.data.tournament.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create tournament.");
    } finally { setSaving(false); }
  };

  return (
    <>
      <SportsCategoryNav />
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="tournament-new-page">
        <div className="overline mb-1">NEW TOURNAMENT</div>
        <h1 className="font-display text-4xl mb-2 inline-flex items-center gap-2">
          <Trophy className="w-8 h-8" />Create a tournament
        </h1>
        <p className="opacity-70 mb-6">Any format — from a neighbourhood cricket cup to a charity marathon.</p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Tournament name *">
            <input required value={form.name} onChange={e => set("name", e.target.value)} className="wofy-input" data-testid="input-name" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Sport *">
              <select required value={form.sport} onChange={e => set("sport", e.target.value)} className="wofy-input" data-testid="input-sport">
                <option value="">Select a sport…</option>
                {meta?.categories?.map(cat => (
                  <optgroup key={cat.key} label={cat.label}>
                    {cat.sports.map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>
            <Field label="Format (e.g. Knockout · 8 teams)">
              <input value={form.format} onChange={e => set("format", e.target.value)} className="wofy-input" data-testid="input-format" />
            </Field>
          </div>

          <Field label="Description">
            <textarea rows={5} value={form.description} onChange={e => set("description", e.target.value)}
              placeholder="Who it's for, prizes, why you're organising it…"
              className="wofy-input" data-testid="input-description" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Venue name">
              <input value={form.venue_name} onChange={e => set("venue_name", e.target.value)} className="wofy-input" data-testid="input-venue" />
            </Field>
            <Field label="City">
              <input value={form.city} onChange={e => set("city", e.target.value)} className="wofy-input" data-testid="input-city" />
            </Field>
            <Field label="State / Region">
              <input value={form.state} onChange={e => set("state", e.target.value)} className="wofy-input" data-testid="input-state" />
            </Field>
            <Field label="Country">
              <input value={form.country} onChange={e => set("country", e.target.value)} className="wofy-input" data-testid="input-country" />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Start date">
              <input type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} className="wofy-input" data-testid="input-start" />
            </Field>
            <Field label="End date">
              <input type="date" value={form.end_date} onChange={e => set("end_date", e.target.value)} className="wofy-input" data-testid="input-end" />
            </Field>
            <Field label="Registration deadline">
              <input type="date" value={form.registration_deadline} onChange={e => set("registration_deadline", e.target.value)} className="wofy-input" data-testid="input-regdeadline" />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Entry fee">
              <input type="number" min="0" value={form.entry_fee_amount} onChange={e => set("entry_fee_amount", e.target.value)} className="wofy-input" data-testid="input-fee" />
            </Field>
            <Field label="Currency">
              <input value={form.entry_fee_currency} onChange={e => set("entry_fee_currency", e.target.value)} className="wofy-input" maxLength={4} data-testid="input-currency" />
            </Field>
            <Field label="Categories (comma-separated)">
              <input value={form.categories} onChange={e => set("categories", e.target.value)} placeholder="Under 15, Open, Women's" className="wofy-input" data-testid="input-categories" />
            </Field>
          </div>

          <Field label="Rules">
            <textarea rows={4} value={form.rules} onChange={e => set("rules", e.target.value)} className="wofy-input" data-testid="input-rules" />
          </Field>

          <Field label="Cover image URL">
            <input value={form.cover_image_url} onChange={e => set("cover_image_url", e.target.value)}
              placeholder="https://…" className="wofy-input" data-testid="input-cover" />
          </Field>

          {/* Photos + Videos, side-by-side captions per spec */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2 inline-flex items-center gap-1">
                <ImageIcon className="w-3.5 h-3.5" /> Upload photos (URLs, one per line)
              </div>
              <textarea rows={4} value={form.photo_urls} onChange={e => set("photo_urls", e.target.value)}
                placeholder="https://…jpg" className="wofy-input font-mono text-xs" data-testid="input-photos" />
              <p className="text-xs opacity-60 mt-1">Up to 24 photos. Paste hosted URLs.</p>
            </div>
            <div>
              <VideoLinkManager value={videoUrls} onChange={setVideoUrls} max={8}
                helper="Paste YouTube or Vimeo links. They'll be embedded on this page next to your photos." />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Contact email">
              <input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} className="wofy-input" data-testid="input-email" />
            </Field>
            <Field label="Contact phone">
              <input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} className="wofy-input" data-testid="input-phone" />
            </Field>
          </div>

          <div className="pt-2">
            <button disabled={saving} className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Publish tournament
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}
